import { useEffect, useMemo, useState } from 'react'
import RampEditor from './RampEditor'
import OverlayStyleEditor from './OverlayStyleEditor'
import { infraStyleFor } from '../utils/overlayStyles'
import { HIDEABLE_SECTIONS } from '../utils/sidebarSections'
import '../styles/SettingsPanel.css'

// Collapsible category drawer with a highlighted (gold) title — mirrors the
// actions-menu section look. Force-open while searching.
function Drawer({ name, forceOpen, children }) {
  const [open, setOpen] = useState(true)
  const isOpen = forceOpen || open
  return (
    <section className="pr-drawer">
      <button type="button" className="pr-drawer-head" onClick={() => setOpen((v) => !v)} aria-expanded={isOpen}>
        <span className="pr-drawer-title">{name}</span>
        <span className="pr-drawer-chevron">{isOpen ? '▾' : '▸'}</span>
      </button>
      {isOpen && <div className="pr-drawer-body">{children}</div>}
    </section>
  )
}

// Display-settings popup modal (opened by the cog). Categories match the
// actions-menu names, each a collapsible drawer; a search bar filters every
// option. Also hosts the theme toggle, login/logout, and the "Désactiver
// éléments" toggles that hide actions-menu sections.
export default function SettingsPanel({
  open, onClose,
  theme, onToggleTheme, isLoggedIn, loginUrl, logoutUrl,
  hiddenSections, onToggleSection,
  // Frontières
  borderMode, onBorderModeChange, borderWidth, onBorderWidthChange,
  borderColor, onBorderColorChange, borderOpacity, onBorderOpacityChange,
  borderStyle, onBorderStyleChange, haloEnabled, onToggleHalo, haloColor, onHaloColorChange,
  // Carte à bulles
  bubbleColor, onBubbleColorChange, bubbleSize, onBubbleSizeChange,
  // Monde réel + Éléments RP
  overlayStyles, onUpdateOverlayStyle, onResetOverlayStyle,
  infraTypes, onUpdateInfraStyle, onResetInfraStyle,
}) {
  const [q, setQ] = useState('')
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  useEffect(() => { if (open) setQ('') }, [open])

  const groups = useMemo(() => {
    const G = {}
    const add = (section, label, keywords, node) => {
      (G[section] = G[section] || []).push({ label, keywords: `${label} ${keywords}`.toLowerCase(), node })
    }

    // ── Frontières ────────────────────────────────────────────────────────
    add('Frontières', 'Mode des frontières', 'bordure contour région pays aucune', (
      <div className="filter-group">
        <label>Mode des frontières</label>
        <select className="main-select" value={borderMode} onChange={(e) => onBorderModeChange(e.target.value)}>
          <option value="none">Aucune</option>
          <option value="region">Frontières des régions</option>
          <option value="country">Frontières des pays</option>
        </select>
      </div>
    ))
    if (borderMode !== 'none') {
      add('Frontières', 'Épaisseur des frontières', 'largeur trait', (
        <div className="filter-group">
          <label>Épaisseur des frontières</label>
          <input type="range" min="1" max="4" step="0.5" value={borderWidth} onChange={(e) => onBorderWidthChange(parseFloat(e.target.value))} />
          <span className="range-value">{borderWidth}px</span>
        </div>
      ))
      add('Frontières', 'Couleur des frontières', 'teinte', (
        <div className="filter-group">
          <label>Couleur des frontières</label>
          <div className="color-picker-container">
            <input type="color" className="color-picker" value={borderColor} onChange={(e) => onBorderColorChange(e.target.value)} />
            <span className="color-value">{borderColor}</span>
          </div>
        </div>
      ))
      add('Frontières', 'Opacité des frontières', 'transparence', (
        <div className="filter-group">
          <label>Opacité des frontières</label>
          <input type="range" min="0" max="100" step="5" value={Math.round((borderOpacity ?? 1) * 100)} onChange={(e) => onBorderOpacityChange(parseInt(e.target.value, 10) / 100)} />
          <span className="range-value">{Math.round((borderOpacity ?? 1) * 100)}%</span>
        </div>
      ))
      add('Frontières', 'Style des frontières', 'plein tirets pointillés tiret-point dash', (
        <div className="filter-group">
          <label>Style des frontières</label>
          <select className="main-select" value={borderStyle || 'solid'} onChange={(e) => onBorderStyleChange(e.target.value)}>
            <option value="solid">Plein</option>
            <option value="dashed">Tirets</option>
            <option value="dotted">Pointillés</option>
            <option value="mixed">Tiret-point</option>
          </select>
        </div>
      ))
    }
    add('Frontières', 'Halo du pays', 'contour lumineux glow surbrillance', (
      <label className="layer-toggle">
        <input type="checkbox" className="pr-check" checked={!!haloEnabled} onChange={onToggleHalo} />
        <span>Halo du pays (contour lumineux)</span>
      </label>
    ))
    if (haloEnabled) {
      add('Frontières', 'Couleur du halo du pays', 'glow teinte', (
        <div className="filter-group">
          <label>Couleur du halo</label>
          <div className="color-picker-container">
            <input type="color" className="color-picker" value={haloColor || '#D5B654'} onChange={(e) => onHaloColorChange(e.target.value)} />
            <span className="color-value">{haloColor}</span>
          </div>
        </div>
      ))
    }

    // ── Palette & classes ─────────────────────────────────────────────────
    add('Palette & classes', 'Palette & classification', 'couleur choroplèthe viridis inferno quantile percentile classes ramp', <RampEditor />)

    // ── Carte à bulles ────────────────────────────────────────────────────
    add('Carte à bulles', 'Couleur des bulles', 'bulle cercle proportionnel teinte', (
      <div className="filter-group">
        <label>Couleur des bulles</label>
        <div className="color-picker-container">
          <input type="color" className="color-picker" value={bubbleColor || '#4FD0E0'} onChange={(e) => onBubbleColorChange(e.target.value)} />
          <span className="color-value">{bubbleColor}</span>
        </div>
      </div>
    ))
    add('Carte à bulles', 'Taille des bulles', 'bulle cercle rayon échelle', (
      <div className="filter-group">
        <label>Taille des bulles</label>
        <input type="range" min="0.5" max="2" step="0.1" value={bubbleSize || 1} onChange={(e) => onBubbleSizeChange(parseFloat(e.target.value))} />
        <span className="range-value">×{(bubbleSize || 1).toFixed(1)}</span>
      </div>
    ))

    // ── Monde réel (overlay styles) ───────────────────────────────────────
    if (overlayStyles) {
      const overlay = (key, sub, kw, extra = {}) => (
        <div className="settings-labeled">
          <span className="settings-sub">{sub}</span>
          <OverlayStyleEditor
            color={overlayStyles[key].color} width={overlayStyles[key].width}
            onColor={(c) => onUpdateOverlayStyle(key, 'color', c)} onWidth={(w) => onUpdateOverlayStyle(key, 'width', w)}
            onReset={() => onResetOverlayStyle(key)} {...extra} />
        </div>
      )
      add('Monde réel', 'Style des villes', 'ville point forme couleur taille', overlay('cities', 'Villes', '', {
        widthLabel: 'Taille', shape: overlayStyles.cities.shape, onShape: (s) => onUpdateOverlayStyle('cities', 'shape', s),
      }))
      add('Monde réel', 'Style des routes & autoroutes', 'route autoroute ligne couleur épaisseur tirets', overlay('roads', 'Routes & autoroutes', '', {
        dash: overlayStyles.roads.dash, onDash: (d) => onUpdateOverlayStyle('roads', 'dash', d),
      }))
      add('Monde réel', 'Style des voies ferrées', 'train rail chemin de fer ligne couleur épaisseur tirets', overlay('railways', 'Voies ferrées', '', {
        dash: overlayStyles.railways.dash, onDash: (d) => onUpdateOverlayStyle('railways', 'dash', d),
      }))
      add('Monde réel', 'Style de l’hydrographie', 'eau fleuve rivière lac réservoir couleur épaisseur opacité', overlay('hydrography', 'Fleuves, rivières & lacs', '', {
        opacity: overlayStyles.hydrography.opacity, onOpacity: (o) => onUpdateOverlayStyle('hydrography', 'opacity', o),
      }))
    }

    // ── Éléments RP (infrastructure styles — every catalogue type) ────────
    const types = (infraTypes || []).map((t) => t.type || t).filter(Boolean)
    if (overlayStyles && types.length) {
      types.forEach((type) => {
        const s = infraStyleFor(overlayStyles, type)
        add('Éléments RP', `Style — ${type}`, 'infrastructure tracé ligne couleur épaisseur route autoroute chemin fer', (
          <div className="settings-labeled">
            <span className="settings-sub">{type}</span>
            <OverlayStyleEditor color={s.color} width={s.width} dash={s.dash}
              onDash={(d) => onUpdateInfraStyle(type, 'dash', d)} onColor={(c) => onUpdateInfraStyle(type, 'color', c)}
              onWidth={(w) => onUpdateInfraStyle(type, 'width', w)} onReset={() => onResetInfraStyle(type)} />
          </div>
        ))
      })
    }

    // ── Désactiver éléments (hide actions-menu sections) ──────────────────
    const hidden = new Set(hiddenSections || [])
    HIDEABLE_SECTIONS.forEach((sec) => {
      add('Désactiver éléments', sec.label, 'masquer cacher afficher section menu', (
        <label className="layer-toggle">
          <input type="checkbox" className="pr-check" checked={!hidden.has(sec.id)} onChange={() => onToggleSection(sec.id)} />
          <span>{sec.label}</span>
        </label>
      ))
    })

    return G
  }, [
    borderMode, borderWidth, borderColor, borderOpacity, borderStyle, haloEnabled, haloColor,
    bubbleColor, bubbleSize, overlayStyles, infraTypes, hiddenSections,
    onBorderModeChange, onBorderWidthChange, onBorderColorChange, onBorderOpacityChange, onBorderStyleChange,
    onToggleHalo, onHaloColorChange, onBubbleColorChange, onBubbleSizeChange,
    onUpdateOverlayStyle, onResetOverlayStyle, onUpdateInfraStyle, onResetInfraStyle, onToggleSection,
  ])

  if (!open) return null

  const query = q.trim().toLowerCase()
  const order = ['Frontières', 'Palette & classes', 'Carte à bulles', 'Monde réel', 'Éléments RP', 'Désactiver éléments']

  return (
    <div className="pr-modal-overlay" onClick={onClose}>
      <div className="pr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pr-modal-header">
          <h2 className="pr-modal-title">Réglages d&apos;affichage</h2>
          <div className="pr-modal-actions">
            <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" onClick={onToggleTheme} title="Basculer clair / sombre">
              {theme === 'light' ? '🌙 Sombre' : '☀️ Clair'}
            </button>
            {isLoggedIn
              ? <a className="pr-btn pr-btn-outline-gold pr-btn-sm" href={logoutUrl}>Déconnexion</a>
              : <a className="pr-btn pr-btn-primary pr-btn-sm" href={loginUrl}>Connexion</a>}
            <button type="button" className="pr-modal-close" onClick={onClose} aria-label="Fermer">×</button>
          </div>
        </div>

        <div className="settings-search">
          <input type="text" placeholder="Rechercher une option…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        </div>

        <div className="pr-modal-body">
          {(() => {
            let any = false
            const rendered = order.map((name) => {
              const items = (groups[name] || []).filter((it) => !query || it.keywords.includes(query))
              if (!items.length) return null
              any = true
              return (
                <Drawer key={name} name={name} forceOpen={!!query}>
                  {items.map((it, i) => <div key={i} className="pr-drawer-item">{it.node}</div>)}
                </Drawer>
              )
            })
            return any ? rendered : <p className="section-hint">Aucune option ne correspond.</p>
          })()}
        </div>
      </div>
    </div>
  )
}
